export type InquiryCenterExperience =
  | "FINAL_TRIAGE_WORKBENCH"
  | "CORE_BUSINESS_FLOW";

export interface InquiryCenterRouteInput {
  requestedMode?: string;
  inquiryRead?: boolean;
  inquiryReview?: boolean;
  triageOnly?: boolean;
  triageDispositionManage?: boolean;
  emailIntelligenceRead?: boolean;
}

/**
 * Preserve the entry URL across the local UAT login handoff.  The login page
 * is rendered in-place, so dropping the current query would send a user who
 * opened the triage workbench back to the overview after authentication.
 */
export function resolveLocalUatPostLoginUrl({
  pathname,
  search,
  hash,
}: {
  pathname: string;
  search?: string;
  hash?: string;
}): string {
  const returnUrl = `${pathname || "/"}${search ?? ""}${hash ?? ""}`;
  return returnUrl || "/?view=overview";
}

const LEGACY_INQUIRY_VIEWS = new Set(["inbox", "risk"]);

export interface InquiryExperiencePermissions {
  inquiryRead?: boolean;
  inquiryReview?: boolean;
  triageOnly?: boolean;
  triageDispositionManage?: boolean;
  emailIntelligenceRead?: boolean;
}

export function usesFullInquiryExperience(
  permissions?: InquiryExperiencePermissions,
): boolean {
  return Boolean(
    permissions?.inquiryRead ||
      permissions?.inquiryReview ||
      permissions?.triageOnly ||
      permissions?.triageDispositionManage ||
      permissions?.emailIntelligenceRead,
  );
}

/**
 * Full-experience users have one user-facing inquiry route. Older bookmarks and shell
 * commands may still carry the historical Core inbox view; normalize those
 * links before App renders the legacy workspace.
 */
export function canonicalizeInquiryCenterView(
  requestedView: string,
  permissions?: InquiryExperiencePermissions,
): string {
  if (
    usesFullInquiryExperience(permissions) &&
    LEGACY_INQUIRY_VIEWS.has(requestedView)
  ) {
    return "inquiry-center";
  }
  return requestedView;
}

/**
 * Resolve the one canonical Inquiry Center experience without exposing the
 * historical real-shadow mode as a user-facing requirement.
 */
export function resolveInquiryCenterExperience({
  requestedMode,
  inquiryRead = false,
  inquiryReview = false,
  triageOnly = false,
  triageDispositionManage = false,
  emailIntelligenceRead = false,
}: InquiryCenterRouteInput): InquiryCenterExperience {
  if (
    requestedMode === "real-shadow" ||
    usesFullInquiryExperience({
      inquiryRead,
      inquiryReview,
      triageOnly,
      triageDispositionManage,
      emailIntelligenceRead,
    })
  ) {
    return "FINAL_TRIAGE_WORKBENCH";
  }

  return "CORE_BUSINESS_FLOW";
}
